import keys from 'lodash.keys';
import retryPromise from '@chama/utils/functions/retry-promise';
import timeoutPromise from '@chama/utils/functions/timeout-promise';
import AppInsights from '@app/appInsights';
import config from '../config';
import Raven from '../sentry';

const {
  api: {
    dt: { host, timeout },
  },
} = config;
const urlWithPath = path => `https://${host}/${path}`;
const noResponseStatuses = [202, 204];

const addActionBreadCrumb = actionName => {
  Raven.captureBreadcrumb({
    message: `Execute ${actionName}`,
    category: 'action',
  });
};

const queryBuilder = params => {
  return keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
};

const fetchWithRetryAndTimeout = (url, options, onRetryScheduled, onRetrySucceeded) =>
  retryPromise(
    () => {
      const fetchWithTimeout = timeoutPromise(fetch(url, options), timeout);
      return fetchWithTimeout.then(response => {
        // retry when there is a server error
        if (response.status >= 500 && response.status < 600) {
          // TODO: gold-plating: use specialised error to embed response so that the
          // retry promise always results in the response.
          throw new Error('Server error occurred, retrying');
        }
        return response;
      });
    },
    onRetryScheduled,
    onRetrySucceeded,
  );

const track = (trackingData, eventName) => {
  AppInsights.trackEvent(eventName, trackingData);
};

class ApiClient {
  cacheBuster = new Date().getTime();

  signIn(account, username, password) {
    this.account = account;
    this.username = username;
    this.password = password;
  }

  signInWithToken(getTokenCallback) {
    this.getTokenFunc =
      typeof getTokenCallback === 'function' ? getTokenCallback : async () => getTokenCallback;
  }

  signOut() {
    this.account = undefined;
    this.username = undefined;
    this.password = undefined;
  }

  async getRequestHeader(authWithApiKey) {
    if (authWithApiKey) {
      return { Authorization: `apikey ${BACKEND_API_KEY}` };
    }

    const token = await this.getTokenFunc();
    const Authorization = `Bearer ${token}`;

    return {
      Authorization,
      'Content-Type': 'application/json',
    };
  }

  dealerApiFetch(
    path,
    requestMethod = 'GET',
    requestBody,
    authWithApiKey = false,
    withPath = true,
  ) {
    const method = requestMethod.toUpperCase();
    const body = requestBody ? JSON.stringify(requestBody) : undefined;

    return async responseHandler => {
      const headers = await this.getRequestHeader(authWithApiKey);
      return fetchWithRetryAndTimeout(
        withPath ? urlWithPath(path) : path,
        { method, headers, body },
        this.onRetryScheduled,
        this.onRetrySucceeded,
      ).then(response => {
        if (responseHandler) {
          return responseHandler(response);
        }

        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) {
            return Promise.reject(response);
          }

          const { status, statusText } = response;
          const errorMessage = `[${method.toUpperCase()} ${status} - ${statusText}]`;

          throw new Error(errorMessage);
        }

        if (noResponseStatuses.indexOf(response.status) !== -1) {
          return Promise.resolve(response);
        }

        return response.json();
      });
    };
  }

  fetchFile(path) {
    const headers = this.getRequestHeader();

    return fetch(path, { method: 'get', headers }).then(response => {
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) {
          return Promise.reject(response);
        }

        throw new Error('Failed to download file');
      }

      return response.blob();
    });
  }

  patchTicket(ticketId, body) {
    return this.dealerApiFetch(`tickets/${ticketId}`, 'PATCH', body)();
  }

  fetchTicketsByQueue(queue) {
    this.cacheBuster += 1;
    if (!queue) throw Error('api#fetchTicketsByCategory: missing "queue" property');

    return this.dealerApiFetch(`tickets/get-by-queue/${queue}?_=${this.cacheBuster}`)();
  }

  async fetchOrderDetails(dealerId, orderId) {
    if (!orderId) throw Error('api#fetchOrderDetails: orderId is missing');
    if (!dealerId) throw Error('api#fetchOrderDetails: dealerId is missing');

    const orderDetails = await this.dealerApiFetch(`orders/${orderId}?shopId=${dealerId}`)();

    return orderDetails;
  }

  // @since based on modificationTime
  fetchTicketsSince(since) {
    this.cacheBuster += 1;
    return this.dealerApiFetch(`tickets/?_=${this.cacheBuster}${since ? `&since=${since}` : ''}`)(
      response => {
        if (response.status === 401) {
          // TODO: i18n
          throw new Error('Wrong account, username or password');
        }
      },
    );
  }

  // @before based on creationTime
  fetchTicketsBefore(before) {
    this.cacheBuster += 1;
    return this.dealerApiFetch(
      `tickets/?_=${this.cacheBuster}${before ? `&before=${before}` : ''}`,
    )(response => {
      if (response.status === 401) {
        // TODO: i18n
        throw new Error('Wrong account, username or password');
      }
    });
  }

  fetchOrderPeriods = dealerId => {
    return this.dealerApiFetch(`dealersfinances/${dealerId}/periods`)();
  };

  fetchMoreOrderPeriods = href => {
    // This is a temporary fix because we're setting different API hosts in deploy time
    const pureHostAddress = host.replace('/api', '').replace('/v25', '');

    return this.dealerApiFetch(`https://${pureHostAddress}${href}`, 'get', null, false, false)();
  };

  fetchMoreHistoryOrders = href => {
    // This is a temporary fix because we're setting different API hosts in deploy time
    const pureHostAddress = host.replace('/api', '').replace('/v25', '');

    return this.dealerApiFetch(`https://${pureHostAddress}${href}`, 'get', null, false, false)();
  };

  fetchOrderHistoryForPeriod = href => {
    // This is a temporary fix because we're setting different API hosts in deploy time
    const pureHostAddress = host.replace('/api', '').replace('/v25', '');

    return this.dealerApiFetch(`https://${pureHostAddress}${href}`, 'get', null, false, false)();
  };

  fetchHistoryOrderDetail = href => {
    // This is a temporary fix because we're setting different API hosts in deploy time
    const pureHostAddress = host.replace('/api', '').replace('/v25', '');

    return this.dealerApiFetch(`https://${pureHostAddress}${href}`, 'get', null, false, false)();
  };

  fetchSingleTicket = ticketId => {
    return this.dealerApiFetch(`tickets/${ticketId}`)();
  };

  fetchOrderEventHistory = (dealerId, orderId) => {
    return this.dealerApiFetch(`dealersorders/${dealerId}/orders/${orderId}?loadHistory=true`)();
  };

  assignTicketToCurrentUser = async (ticketId, eTag, trackingData) => {
    track(trackingData, 'mdt-order-assign-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      isAssignedToCurrentUser: true,
    });
    track(trackingData, 'mdt-order-assign-request-ended');

    Raven.captureBreadcrumb({
      message: 'Assign ticket',
      category: 'action',
    });

    return result;
  };

  unAssignTicketToCurrentUser = async (ticketId, eTag, trackingData) => {
    track(trackingData, 'mdt-order-unassign-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      isAssignedToCurrentUser: false,
    });
    track(trackingData, 'mdt-order-unassign-request-ended');

    Raven.captureBreadcrumb({
      message: 'Unassign ticket',
      category: 'action',
    });

    return result;
  };

  startDelivery = async (ticketId, eTag, etaMinutes, etaWindowMinutes, driverId, trackingData) => {
    const action = 'acceptOrder';
    addActionBreadCrumb(action);

    track(trackingData, 'mdt-order-accept-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      execution: {
        action,
        parameters: {
          etaMinutes,
          etaWindowMinutes,
          driverId,
        },
      },
    });
    track(trackingData, 'mdt-order-accept-request-ended');

    return result;
  };

  declineOrder = async (ticketId, eTag, reason, trackingData) => {
    const action = 'declineOrder';
    const parameters = reason ? { ...reason } : undefined;
    addActionBreadCrumb(action);

    track(trackingData, 'mdt-order-decline-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      execution: { action, parameters },
    });
    track(trackingData, 'mdt-order-decline-request-ended');

    return result;
  };

  deliverOrder = async (ticketId, eTag, trackingData) => {
    const action = 'deliverOrder';
    addActionBreadCrumb(action);

    track(trackingData, 'mdt-order-deliver-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      execution: { action },
    });
    track(trackingData, 'mdt-order-deliver-request-ended');

    return result;
  };

  cancelOrder = async (ticketId, eTag, reason, trackingData) => {
    const action = 'cancelOrder';
    const parameters = reason ? { ...reason } : undefined;
    addActionBreadCrumb(action);

    track(trackingData, 'mdt-order-cancel-request-started');
    const result = await this.patchTicket(ticketId, {
      eTag,
      execution: { action, parameters },
    });
    track(trackingData, 'mdt-order-cancel-request-ended');

    return result;
  };

  dismissOrder(ticketId, eTag) {
    const action = 'dismissOrder';

    addActionBreadCrumb(action);

    return this.patchTicket(ticketId, {
      eTag,
      execution: { action },
    });
  }

  cancelDelivery(ticketId, eTag) {
    const action = 'cancelDelivery';

    addActionBreadCrumb(action);

    return this.patchTicket(ticketId, {
      eTag,
      execution: { action },
    });
  }

  updateDealerData(dealerId, updatedData) {
    Raven.captureBreadcrumb({
      message: 'Update dealer data',
      category: 'action',
      data: updatedData,
    });

    return this.dealerApiFetch(`dealersadmin/${dealerId}`, 'PATCH', updatedData)();
  }

  updateSeveralDealersData(dealerList, updatedData) {
    const mapMeaningfulSentryInfo = dealer => {
      return {
        id: dealer.id,
        name: dealer.name,
        termsAndConditions: dealer.termsAndConditions,
      };
    };
    const mapPromiseList = dealer => this.updateDealerData(dealer.id, updatedData);

    Raven.captureBreadcrumb({
      message: 'Update several dealers data',
      category: 'action',
      data: {
        updatedData,
        dealerList: dealerList.map(mapMeaningfulSentryInfo),
      },
    });

    return Promise.all(dealerList.map(mapPromiseList));
  }

  onRetryScheduled(callback) {
    this.onRetryScheduled = callback;
  }

  onRetrySucceeded(callback) {
    this.onRetrySucceeded = callback;
  }

  getUserKind() {
    return this.dealerApiFetch('dealersadmin/auth', 'post')();
  }

  getNearbyDealers(lat, lon) {
    return this.dealerApiFetch(`dealers?lat=${lat}&lon=${lon}`, 'get')();
  }

  downloadInvoice(dealerId, periodTimestamp) {
    const path = urlWithPath(
      `dealersfinances/${dealerId}/invoice?periodReference=${periodTimestamp}`,
    );

    return this.fetchFile(path);
  }

  downloadPaymentSlip(dealerId, periodTimestamp) {
    const path = urlWithPath(
      `dealersfinances/${dealerId}/boleto?periodReference=${periodTimestamp}`,
    );

    return this.fetchFile(path);
  }

  getNotaFiscalLink(dealerId, periodTimestamp) {
    return this.dealerApiFetch(
      `dealersfinances/${dealerId}/notafiscal?periodReference=${periodTimestamp}`,
    )();
  }

  fetchDrivers(dealerId, lat, lon) {
    Raven.captureBreadcrumb({
      message: 'Fetch drivers',
      category: 'action',
    });

    if (!lat) throw Error('Error: Lat is required');
    if (!lon) throw Error('Error: Lon is required');

    const queryString = `?${queryBuilder({ lat, lon })}`;

    return this.dealerApiFetch(
      urlWithPath(`drivers/${dealerId}/relative-to-location${queryString}`),
    )(res => {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Error: Incorrect dealerId.');
      }
    });
  }

  enrichToken() {
    return this.dealerApiFetch(`authentication/sign-in`, 'POST')();
  }

  getPermissions() {
    return this.dealerApiFetch(`authentication/permissions-matrix`, 'get')();
  }

  // Temp
  testPermission() {
    return this.dealerApiFetch(`authentication/requires-full-access-permission`)();
  }
}

export default ApiClient;



// WEBPACK FOOTER //
// ./src/api/api-client.js